<script setup lang="ts">
import type { IMAGE_FIT } from '@pkg/sanity-utils/constants/imageFit';
import type { Image } from '@sanity/types';
import type { ImageOptions } from '@pkg/ui/composables/useSanityImage';
import { useSanityImage } from '@pkg/ui/composables/useSanityImage';
import { IMAGE_SIZES } from '@pkg/ui/constants/imageSizes';
/**
 * [1] Alt text may be defined on the image itself.
 * [2] Alt text can be passed in to override what's on the image, or as a fallback in case nothing is defined on the image.
 * [3] Above the fold images should be 'eager'. All else should be 'lazy'. Defaults to 'lazy'.
 */
const props = defineProps<{
  image?: Image & { alt?: string, fit?: IMAGE_FIT } /* [1] */ | null;
  height?: number;
  width?: number;
  loading?: 'lazy' | 'eager'; /* [3] */
  imageOptions?: ImageOptions;
}>();

const proportionalHeight = () => {
  if (!props.height || !props.width) { return props.height; }
  const aspectRatio = props.height / props.width;

  return Math.round(IMAGE_SIZES.MAX_IMG_WIDTH * aspectRatio);
};

// @todo pass relevant options through to useSanityImage
const { src, srcset, sizes } = useSanityImage(
  props.image,
  { maxWidth: props.width || undefined },
  {
    // We need to pass in width and height for hotspot to work
    width: props.width && IMAGE_SIZES.MAX_IMG_WIDTH > props.width ? props.width : IMAGE_SIZES.MAX_IMG_WIDTH,
    height: props.width && IMAGE_SIZES.MAX_IMG_WIDTH > props.width ? props.height : proportionalHeight(),
    ...props.imageOptions,
  },
);

if (props.loading === 'eager' && src) {
  useHead({
    link: [
      { rel: 'preload', href: src, as: 'image', fetchpriority: 'high', imagesrcset: srcset, imagesizes: sizes },
    ],
  });
}

// @todo add blurhash if present in image

let hotspotStyle = '';
if (props.image?.hotspot) {
  hotspotStyle = `background-position: ${props.image.hotspot.x * 100}% ${props.image.hotspot.y * 100}%`;
}
</script>

<template>
  <div
    v-if="src"
    class="c-parallax-image"
    :style="`background-image: url(${src}); ${hotspotStyle}`"
  />
</template>

<style scoped>
.c-parallax-image {
  height: 360px;
  position: relative;
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  @supports (-webkit-touch-callout: none) {
    /* Parallax disabled on iOS devices as its not supported: https://caniuse.com/background-attachment */
    background-attachment: initial;
  }

  @screen tablet-wide {
    height: 540px;
  }

  @screen desk {
    height: 640px;
  }
}
</style>
